/**
 * 数学工具：向量/角度/随机/插值/缓动。
 * 纯 ESM，不依赖浏览器 API，node 可直接 import。
 */

/** 圆周率两倍 */
export const TAU = Math.PI * 2;

/**
 * 将 v 限制在 [min, max]
 * @param {number} v
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function clamp(v, min, max) {
  return v < min ? min : v > max ? max : v;
}

/**
 * 线性插值 a→b
 * @param {number} a
 * @param {number} b
 * @param {number} t 0..1
 * @returns {number}
 */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/** 当前随机源（默认 Math.random；录像/回放时注入确定性 PRNG） */
let _rng = Math.random;

/**
 * 注入随机源。gameScene 按局种子设置 mulberry32，退出时恢复默认。
 * @param {?() => number} fn null/省略 恢复 Math.random
 */
export function setRng(fn) {
  _rng = fn || Math.random;
}

/**
 * mulberry32 确定性 PRNG（录像/回放用，与种子一一对应）。
 * @param {number} seed 32 位整数种子
 * @returns {() => number} [0,1) 随机函数
 */
export function mulberry32(seed) {
  let a = seed | 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * [0, 1) 随机数
 * @returns {number}
 */
export function rand() {
  return _rng();
}

/**
 * [min, max) 随机浮点
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function randRange(min, max) {
  return min + _rng() * (max - min);
}

/**
 * [min, max] 随机整数（含两端）
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function randInt(min, max) {
  return min + Math.floor(_rng() * (max - min + 1));
}

/**
 * 从数组中随机取一个元素
 * @template T
 * @param {T[]} arr
 * @returns {T}
 */
export function pick(arr) {
  return arr[Math.floor(_rng() * arr.length)];
}

/**
 * 从 (x1,y1) 指向 (x2,y2) 的弧度角
 * @returns {number}
 */
export function angleTo(x1, y1, x2, y2) {
  return Math.atan2(y2 - y1, x2 - x1);
}

/**
 * 两点距离
 * @returns {number}
 */
export function dist(x1, y1, x2, y2) {
  return Math.hypot(x2 - x1, y2 - y1);
}

/**
 * 两点距离平方（免开方）
 * @returns {number}
 */
export function dist2(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return dx * dx + dy * dy;
}

/**
 * 向量长度
 * @param {number} x
 * @param {number} y
 * @returns {number}
 */
export function len(x, y) {
  return Math.hypot(x, y);
}

/**
 * 把角度归一化到 (-PI, PI]
 * @param {number} a 弧度
 * @returns {number}
 */
export function normAngle(a) {
  a %= TAU;
  if (a <= -Math.PI) a += TAU;
  else if (a > Math.PI) a -= TAU;
  return a;
}

/**
 * easeOutCubic：快出缓停
 * @param {number} t 0..1
 * @returns {number}
 */
export function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * easeInCubic：慢启动
 * @param {number} t 0..1
 * @returns {number}
 */
export function easeInCubic(t) {
  return t * t * t;
}

/**
 * easeInOutQuad：两头缓
 * @param {number} t 0..1
 * @returns {number}
 */
export function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/**
 * easeOutBack：过冲回弹
 * @param {number} t 0..1
 * @returns {number}
 */
export function easeOutBack(t) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}
